import React, { useState } from 'react';
import {
  Save,
  Edit3,
  Plus,
  Settings,
  X,
  Check,
  Trash2,
  ChevronUp,
  ChevronDown,
  Lightbulb,
  Wallet,
  TriangleAlert,
  CheckCircle2,
  Loader2,
  Clock,
} from 'lucide-react';

import { getAuth } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

import Card from './common/Card';
import Button from './common/Button';
import Badge from './common/Badge';
import Modal from './common/Modal';
import SectionLabel from './common/SectionLabel';
import SafeChart from './common/SafeChart';
import EditableInput from './common/EditableInput';
import GlowArea from './charts/GlowArea';
import GlowDonut from './charts/GlowDonut';
import useChartTheme from './charts/useChartTheme';
import AffiliateCard from './common/AffiliateCard';
import { AFFILIATE_LINKS } from '../config/affiliateLinks';
import { db } from '../authentication/firebase-config';
import {
  hasUnsavedInvestmentChanges,
  stableStringify,
} from './investments/investmentWriteGuard';

/**
 * Effective USD value of a connected wallet.
 *
 * Mirrors the rule the Wallets tab uses to display a wallet's balance: a
 * user-entered manual value overrides the API-fetched value when present.
 * Kept local (not imported) so this tab depends only on the wallet data
 * shape, never on WalletsTab internals.
 */
const walletUsdValue = (w) =>
  typeof w.manualValue === 'number' && !isNaN(w.manualValue)
    ? w.manualValue
    : w.lastValue || 0;

/* ─── Wallet → category assignment persistence ──────────────────────────────
 *
 * Stored in an ISOLATED per-user settings doc, separate from the main
 * financial-data document, mirroring the walletPrivacyService pattern:
 *
 *   users/{uid}/settings/investmentWalletCategories
 *   { assignments: { [walletId]: categoryName }, updatedAt }
 *
 * Keeping it in its own doc means this feature never touches the user's
 * existing financial records — it only ever reads/writes its own key.
 */
const walletCategoriesRef = (uid) =>
  doc(db, 'users', uid, 'settings', 'investmentWalletCategories');

async function fetchWalletCategoryMap(uid) {
  if (!uid) return {};
  try {
    const snap = await getDoc(walletCategoriesRef(uid));
    if (!snap.exists()) return {};
    return snap.data().assignments || {};
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[InvestmentsTab] wallet category read failed:', err.message);
    return {};
  }
}

async function writeWalletCategoryMap(uid, assignments) {
  if (!uid) return;
  try {
    await setDoc(
      walletCategoriesRef(uid),
      { assignments, updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[InvestmentsTab] wallet category write failed:', err.message);
  }
}

/**
 * InvestmentsTab – enhanced version with categories, monthly tracking, and growth charts.
 *
 * Props
 *  ─ investments:          Investment[]
 *  ─ totalInvestments:     number (pre-calculated for speed)
 *  ─ editMode:             boolean
 *  ─ onToggleEdit():       handler
 *  ─ updateInvestment(i,k,val)
 *  ─ addInvestment():      append a blank investment row
 *  ─ chartsReady:          boolean
 *  ─ investmentCategories: string[] - available categories
 *  ─ onUpdateCategories:   handler to update categories
 *  ─ investmentHistory:    object with monthly data by year
 *  ─ updateInvestmentHistory: handler to update monthly values
 */
const InvestmentsTab = ({
  investments,
  totalInvestments,
  editMode,
  onToggleEdit,
  updateInvestment,
  addInvestment,
  chartsReady,
  investmentCategories = ['Emergency Fund', 'Retirement', 'Growth', 'Income', 'Speculative', 'Real Estate', 'Crypto'],
  onUpdateCategories,
  investmentHistory = {},
  updateInvestmentHistory,
  removeInvestment, // Add this prop
  wallets = [], // connected wallets from the Wallets tab (read-only)
  /* ─── ARC-61: committed (server-confirmed) state, explicit save, fail-closed
     reads. All optional — without them the tab behaves exactly as it did
     before, but the shell (FinancialDashboard) supplies them, and only then can
     this tab tell "saved" apart from "typed". */
  committedInvestments,
  committedInvestmentHistory,
  committedTotalInvestments,
  onSaveInvestments,  // async () => { success, error } — performs a real save
  hasUnsavedChanges,  // optional override of the local dirty check
  loadError,          // non-empty when the investment read failed: read-only
}) => {
  const [showCategoryEditor, setShowCategoryEditor] = useState(false);
  const [tempCategories, setTempCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showGrowthChart, setShowGrowthChart] = useState(true);

  /* ─── ARC-61: fail closed ──────────────────────────────────────────────
     A failed read tells us nothing about what the server holds. Every path
     that could write is shut off — not just Save, but editing, adding
     holdings, deleting them, editing categories and the monthly history — so
     there is nothing to lose by refusing to save. */
  const readOnly = Boolean(loadError);
  const canEdit = editMode && !readOnly;

  /* ─── ARC-61: committed state ──────────────────────────────────────────
     The holdings list and the monthly editor below are the edit buffer.
     Everything DERIVED from them — the category summary, the allocation donut,
     the growth chart and the headline portfolio value — reads the last
     SERVER-CONFIRMED state, so a member is never shown a figure that was not
     persisted. Falls back to the buffer when the shell passes no committed
     snapshot (pre-ARC-61 behaviour); after a failed read there is neither. */
  const hasCommitted = Array.isArray(committedInvestments);
  const savedInvestments = hasCommitted ? committedInvestments : investments;
  const savedHistory = hasCommitted
    ? (committedInvestmentHistory || {})
    : investmentHistory;
  const savedTotal =
    typeof committedTotalInvestments === 'number'
      ? committedTotalInvestments
      : totalInvestments;

  const dirty =
    typeof hasUnsavedChanges === 'boolean'
      ? hasUnsavedChanges
      : hasCommitted &&
        hasUnsavedInvestmentChanges(
          { investments, investmentHistory, investmentCategories },
          {
            investments: committedInvestments,
            investmentHistory: committedInvestmentHistory,
            investmentCategories,
          }
        );

  /* ─── ARC-61: explicit save result ─────────────────────────────────────
     { kind: "saving" | "success" | "error", text, signature }. Leaving edit
     mode is not evidence of persistence; every save resolves to one of these. */
  const [saveStatus, setSaveStatus] = useState(null);
  const saving = saveStatus?.kind === 'saving';
  // A "saved" notice describes the buffer as it was when it was written, so it
  // stands only while the buffer is unchanged since that write.
  const bufferSignature = stableStringify({
    investments,
    investmentHistory,
    investmentCategories,
  });
  const savedNoticeStillTrue = !dirty || saveStatus?.signature === bufferSignature;
  const visibleStatus =
    saveStatus?.kind === 'success' && !savedNoticeStillTrue ? null : saveStatus;

  const handleEditSaveClick = async () => {
    if (readOnly || saving) return;

    if (!editMode) {
      setSaveStatus(null);
      if (onToggleEdit) onToggleEdit();
      return;
    }

    // No save handler wired — keep the old toggle, and claim nothing was saved.
    if (typeof onSaveInvestments !== 'function') {
      if (onToggleEdit) onToggleEdit();
      return;
    }

    setSaveStatus({ kind: 'saving', text: 'Saving your changes…' });

    let result;
    try {
      result = await onSaveInvestments();
    } catch (e) {
      result = { success: false, error: e?.message };
    }

    if (result && result.success) {
      setSaveStatus({
        kind: 'success',
        text: 'Changes saved. The figures below come from your saved data.',
        signature: stableStringify({
          investments,
          investmentHistory,
          investmentCategories,
        }),
      });
      if (onToggleEdit) onToggleEdit();
      return;
    }

    /* Failed: stay in edit mode so every entered value stays on screen and
       editable. Some upstream errors already promise the entries survived;
       adding our own to those would print that promise twice. */
    const reassures = (msg) => /still here|still on screen/i.test(msg);
    setSaveStatus({
      kind: 'error',
      text: result?.error
        ? reassures(result.error)
          ? `Not saved: ${result.error}`
          : `Not saved: ${result.error} Your entries are still here — try Save again.`
        : 'Not saved. Your entries are still here — check your connection and try Save again.',
    });
  };

  // Resolved v4 chart palette — the NEON categorical set is reused for the
  // category-summary dots so they match the allocation donut arcs exactly.
  const chartTheme = useChartTheme();

  // Wallet → category assignments: { [walletId]: categoryName }.
  // Persisted in its own isolated Firestore settings doc (see helpers above),
  // so it survives reload without touching the main financial-data document.
  const [walletCategoryMap, setWalletCategoryMap] = useState({});

  // Hydrate the assignment map once on mount.
  React.useEffect(() => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    let cancelled = false;
    fetchWalletCategoryMap(uid).then((map) => {
      if (!cancelled) setWalletCategoryMap(map);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Assign (or clear) a wallet's category. Updates local state immediately and
  // persists in the background (fire-and-forget — a failed write self-heals on
  // next mount). An empty category means "don't include in the pie".
  const assignWalletCategory = (walletId, category) => {
    setWalletCategoryMap((prev) => {
      const next = { ...prev };
      if (!category) delete next[walletId];
      else next[walletId] = category;
      const uid = getAuth().currentUser?.uid;
      if (uid) writeWalletCategoryMap(uid, next);
      return next;
    });
  };

  // Sum each assigned wallet's current USD value into its category bucket.
  // Unassigned wallets are intentionally excluded so on-chain value is only
  // ever counted when the user explicitly opts a wallet in.
  const walletContribByCategory = React.useMemo(() => {
    const out = {};
    wallets.forEach((w) => {
      const category = walletCategoryMap[w.id];
      if (!category) return;
      out[category] = (out[category] || 0) + walletUsdValue(w);
    });
    return out;
  }, [wallets, walletCategoryMap]);

  // Total on-chain value rolled into the pie (assigned wallets only). Added on
  // top of manual entries — see combinedTotal below for the denominator used by
  // category percentages so slices still sum to 100%.
  const assignedWalletsTotal = React.useMemo(
    () => Object.values(walletContribByCategory).reduce((s, v) => s + v, 0),
    [walletContribByCategory]
  );

  // ARC-61: saved manual holdings + assigned on-chain wallets. The manual half
  // is the committed total, so the headline figure is one the server confirmed.
  const combinedTotal = savedTotal + assignedWalletsTotal;

  // Cache the filtered investments with their original indices
  const filteredInvestmentsWithIndices = React.useMemo(() => {
    return investments
      .map((inv, originalIndex) => ({ inv, originalIndex }))
      .filter(({ inv }) => !inv.name.includes('[DELETED]') && inv.amount >= 0);
  }, [investments]);

  // Get available years from investment history.
  // ARC-61: from the buffer AND the saved snapshot, so a year the member has
  // just started editing is selectable and one the server holds never vanishes
  // from the list.
  const availableYears = Array.from(
    new Set([...Object.keys(investmentHistory), ...Object.keys(savedHistory)])
  ).map(year => parseInt(year)).sort((a, b) => b - a);
  if (availableYears.length === 0) {
    availableYears.push(new Date().getFullYear());
  }

  // Prepare growth chart data for selected year
  // ARC-61: plotted from the SAVED history and the saved portfolio total — a
  // growth curve is a claim about persisted data, not about typing.
  const getGrowthChartData = () => {
    const yearData = savedHistory[selectedYear] || {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return months.map(month => {
      const isCurrentMonth = month === new Date().toLocaleDateString('en-US', { month: 'short' }) && selectedYear === new Date().getFullYear();
      return {
        month,
        total: isCurrentMonth ? savedTotal : (yearData[month] || 0),
      };
    });
  };

  // Calculate category totals for summary (manual entries + assigned wallets)
  const getCategorySummary = () => {
    const categoryTotals = {};
    savedInvestments
      .filter(inv => !inv.name.includes('[DELETED]') && inv.amount > 0) // Filter out deleted investments
      .forEach(inv => {
        const category = inv.type || 'Uncategorized';
        categoryTotals[category] = (categoryTotals[category] || 0) + (inv.amount || 0);
      });

    // Roll assigned on-chain wallet values on top of the manual entries.
    Object.entries(walletContribByCategory).forEach(([category, total]) => {
      categoryTotals[category] = (categoryTotals[category] || 0) + total;
    });

    return Object.entries(categoryTotals)
      .map(([category, total]) => ({ category, total }))
      .sort((a, b) => b.total - a.total);
  };

  // Prepare pie chart data grouped by category (manual entries + assigned wallets)
  const getPieChartData = () => {
    const categoryTotals = {};
    savedInvestments
      .filter(inv => !inv.name.includes('[DELETED]') && inv.amount > 0)
      .forEach(inv => {
        const category = inv.type || 'Uncategorized';
        categoryTotals[category] = (categoryTotals[category] || 0) + (inv.amount || 0);
      });

    // Roll assigned on-chain wallet values on top of the manual entries.
    Object.entries(walletContribByCategory).forEach(([category, amount]) => {
      categoryTotals[category] = (categoryTotals[category] || 0) + amount;
    });

    return Object.entries(categoryTotals)
      .map(([category, amount]) => ({ name: category, amount }))
      .sort((a, b) => b.amount - a.amount);
  };

  // Category editor functions
  const openCategoryEditor = () => {
    // ARC-61: categories are written on the same save as the holdings, so a
    // failed read closes this editor off with everything else.
    if (readOnly) return;
    setTempCategories([...investmentCategories]);
    setShowCategoryEditor(true);
  };

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) return;
    
    const categoryName = newCategoryName.trim();
    if (tempCategories.includes(categoryName)) {
      alert('Category already exists!');
      return;
    }
    
    setTempCategories([...tempCategories, categoryName]);
    setNewCategoryName('');
  };

  const handleRemoveCategory = (categoryToRemove) => {
    setTempCategories(tempCategories.filter(cat => cat !== categoryToRemove));
  };

  const handleSaveCategories = () => {
    if (onUpdateCategories) {
      onUpdateCategories(tempCategories);
    }
    setShowCategoryEditor(false);
  };

  const handleCancelCategories = () => {
    setTempCategories([]);
    setNewCategoryName('');
    setShowCategoryEditor(false);
  };

  const moveCategory = (index, direction) => {
    const newCategories = [...tempCategories];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < newCategories.length) {
      [newCategories[index], newCategories[newIndex]] = [newCategories[newIndex], newCategories[index]];
      setTempCategories(newCategories);
    }
  };

  // Add investment with default Emergency Fund category
  const handleAddInvestment = () => {
    // ARC-61: a failed read must issue no writes, and this appends straight
    // into the buffer autosave serialises.
    if (readOnly) return;
    const newInvestment = {
      name: 'New Investment',
      type: 'Emergency Fund', // Default to Emergency Fund
      amount: 0
    };
    addInvestment(newInvestment);
  };

  // Remove investment — uses parent's hard-delete handler (filters from array entirely)
  const handleRemoveInvestment = (index) => {
    if (readOnly) return;
    if (window.confirm('Are you sure you want to remove this investment?')) {
      if (removeInvestment) {
        removeInvestment(index);
      } else {
        // Fallback: legacy soft-delete if parent didn't pass remove handler
        updateInvestment(index, 'amount', 0);
        updateInvestment(index, 'name', '[DELETED] ' + investments[index].name);
      }
    }
  };

  // Auto-update investment history based on current total
  const syncCurrentTotal = () => {
    const currentMonth = new Date().toLocaleDateString('en-US', { month: 'short' });
    const currentYear = new Date().getFullYear();

    if (updateInvestmentHistory) {
      updateInvestmentHistory(currentYear, currentMonth, totalInvestments);
    }
  };

  /* Sync current investments total to growth chart.
     ARC-61: this effect is the tab's second write path into the save payload,
     and it needs no member action at all — it fires on render from whatever is
     in the buffer. That is exactly what made the seeded scaffold so damaging:
     nine invented holdings loaded, and this stamped their $325,000 total into
     `investmentHistory` as a figure indistinguishable from one the member
     entered. After a failed read there is nothing here worth writing and no way
     to know what it would land on, so it does not run. */
  React.useEffect(() => {
    if (readOnly) return;
    if (totalInvestments > 0) {
      syncCurrentTotal();
    }
  }, [totalInvestments, readOnly]);

  // Update monthly investment value
  const updateMonthlyValue = (month, value) => {
    if (readOnly) return;
    if (updateInvestmentHistory) {
      updateInvestmentHistory(selectedYear, month, parseFloat(value) || 0);
    }
  };

  const growthChartData = getGrowthChartData();
  const categorySummary = getCategorySummary();
  const pieChartData = getPieChartData();

  return (
    <div className="space-y-6">
      <Card hover={false} className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-3">
          <h3 className="text-h2 text-arc-ink">Investment Portfolio</h3>

          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={openCategoryEditor} disabled={readOnly}>
              <Settings size={16} /> Categories
            </Button>

            <Button variant="secondary" onClick={handleAddInvestment} disabled={readOnly}>
              <Plus size={16} /> Add&nbsp;Investment
            </Button>

            <Button
              variant="secondary"
              onClick={() => setShowGrowthChart(!showGrowthChart)}
            >
              {showGrowthChart ? 'Hide' : 'Show'} Growth Chart
            </Button>

            {/* ARC-61: Save is a real save with a real result, not a mode toggle. */}
            <Button
              variant="primary"
              onClick={handleEditSaveClick}
              disabled={readOnly || saving}
            >
              {saving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : editMode ? (
                <Save size={16} />
              ) : (
                <Edit3 size={16} />
              )}{' '}
              {saving ? 'Saving…' : editMode ? 'Save' : 'Edit'}
            </Button>
          </div>
        </div>

        {/* ─── ARC-61: failed read ───────────────────────────────────────────
            The tab is read-only and saving is disabled entirely. Writing an
            empty or placeholder portfolio on top of data we failed to read is
            the overwrite this issue exists to prevent. */}
        {readOnly && (
          <div
            role="alert"
            className="flex gap-3 p-4 rounded-arc-card shadow-arc-sm ring-1 bg-arc-neg-soft ring-arc-neg-soft text-arc-neg"
          >
            <TriangleAlert size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="text-[14.5px] leading-[1.55]">{loadError}</div>
          </div>
        )}

        {/* ─── ARC-61: explicit save outcome ─────────────────────────────────
            Leaving edit mode is not confirmation. Every save resolves here to a
            visible saving / saved / not-saved state. On failure the tab stays in
            edit mode and the entered values stay in the buffer. */}
        {visibleStatus && (
          <div
            role="status"
            className={`flex gap-3 p-4 rounded-arc-card shadow-arc-sm ring-1 ${
              visibleStatus.kind === 'error'
                ? 'bg-arc-neg-soft ring-arc-neg-soft text-arc-neg'
                : 'bg-arc-accent-soft ring-arc-line text-arc-ink-2'
            }`}
          >
            {visibleStatus.kind === 'saving' && (
              <Loader2 size={18} className="flex-shrink-0 mt-0.5 animate-spin" aria-hidden="true" />
            )}
            {visibleStatus.kind === 'success' && (
              <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
            )}
            {visibleStatus.kind === 'error' && (
              <TriangleAlert size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
            )}
            <div className="text-[14.5px] leading-[1.55]">{visibleStatus.text}</div>
          </div>
        )}

        {/* ─── ARC-61: unsaved-edit marker ───────────────────────────────────
            The figures below are the saved ones, so say so when the buffer has
            moved past them. */}
        {!readOnly && dirty && (
          <div className="flex gap-3 items-center text-arc-ink-3 text-[13.5px]">
            <Clock size={15} className="flex-shrink-0" aria-hidden="true" />
            <span>
              You have unsaved changes. The summary, charts and total below still
              show your last saved portfolio.
            </span>
          </div>
        )}

        {/* Category Summary */}
        <div className="p-4 bg-arc-paper-2 rounded-arc-ctl ring-1 ring-arc-line">
          <SectionLabel className="mb-3">Portfolio by Category</SectionLabel>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {categorySummary.map(({ category, total }, index) => {
              const dotColor = chartTheme.neon[index % chartTheme.neon.length];
              return (
                <div
                  key={category}
                  className="bg-arc-card p-3 rounded-arc-ctl ring-1 ring-arc-line"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: dotColor, boxShadow: `0 0 6px ${dotColor}` }}
                    />
                    <span className="text-sm font-medium text-arc-ink">{category}</span>
                  </div>
                  <p className="text-stat tnum font-bold text-arc-accent-ink">
                    ${total.toLocaleString()}
                  </p>
                  <p className="text-xs tnum text-arc-ink-3">
                    {(combinedTotal > 0 ? (total / combinedTotal) * 100 : 0).toFixed(1)}% of portfolio
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* On-chain Wallets → category assignment.
            Assign each connected wallet to one category so its current on-chain
            value rolls into the allocation pie below. Whole-wallet only (v1) —
            no per-token splitting. Tracking/education only, not advice. */}
        <div className="p-4 bg-arc-paper-2 rounded-arc-ctl ring-1 ring-arc-line">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <h4 className="flex items-center gap-2 text-h2 text-arc-ink">
              <Wallet size={18} className="text-arc-accent-ink" /> On-chain Wallets
            </h4>
            {assignedWalletsTotal > 0 && (
              <Badge tone="accent">
                ${assignedWalletsTotal.toLocaleString()} included in allocation
              </Badge>
            )}
          </div>
          <p className="text-xs text-arc-ink-3 mb-3">
            Assign a connected wallet to a category to include its current value in
            your allocation view below. For tracking and education only — not
            investment advice. Values come from the Wallets tab; manage wallets there.
          </p>

          {wallets.length === 0 ? (
            <p className="text-sm text-arc-ink-3">
              No wallets connected yet. Add wallets in the Wallets tab to include them here.
            </p>
          ) : (
            <div className="space-y-2">
              {wallets.map((w) => {
                const assigned = walletCategoryMap[w.id] || '';
                return (
                  <div
                    key={w.id}
                    className="flex items-center justify-between gap-3 p-3 bg-arc-card rounded-arc-ctl ring-1 ring-arc-line"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-arc-ink truncate">{w.label || 'Wallet'}</p>
                      <p className="text-xs tnum text-arc-ink-3">
                        ${walletUsdValue(w).toLocaleString()}
                        {!assigned && ' · not included'}
                      </p>
                    </div>
                    <select
                      value={assigned}
                      onChange={(e) => assignWalletCategory(w.id, e.target.value)}
                      className="text-sm bg-arc-card text-arc-ink border border-arc-line rounded-arc-ctl px-2 py-1 focus:outline-none focus:ring-2 focus:ring-arc-accent"
                    >
                      <option value="">— Don't include —</option>
                      {investmentCategories.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Category Editor Modal */}
        <Modal
          open={showCategoryEditor}
          onClose={handleCancelCategories}
          title="Customize Investment Categories"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={handleCancelCategories}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveCategories}>
                <Check size={16} /> Save Changes
              </Button>
            </div>
          }
        >
          {/* Add New Category */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-arc-ink mb-2">Add New Category</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Category name"
                className="flex-1 bg-arc-card text-arc-ink px-3 py-2 border border-arc-line rounded-arc-ctl focus:outline-none focus:ring-2 focus:ring-arc-accent"
                onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
              />
              <Button variant="primary" onClick={handleAddCategory} aria-label="Add category">
                <Plus size={16} />
              </Button>
            </div>
          </div>

          {/* Current Categories */}
          <div>
            <label className="block text-sm font-medium text-arc-ink mb-2">Current Categories</label>
            <div className="space-y-2">
              {tempCategories.map((category, index) => (
                <div key={category} className="flex items-center gap-2 p-2 bg-arc-paper-2 rounded-arc-ctl">
                  <span className="flex-1 text-arc-ink">{category}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => moveCategory(index, 'up')}
                      disabled={index === 0}
                      aria-label="Move up"
                      className="p-1.5 rounded-arc-sm text-arc-ink-3 hover:text-arc-ink hover:bg-arc-card disabled:opacity-40"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      onClick={() => moveCategory(index, 'down')}
                      disabled={index === tempCategories.length - 1}
                      aria-label="Move down"
                      className="p-1.5 rounded-arc-sm text-arc-ink-3 hover:text-arc-ink hover:bg-arc-card disabled:opacity-40"
                    >
                      <ChevronDown size={14} />
                    </button>
                    <button
                      onClick={() => handleRemoveCategory(category)}
                      aria-label="Remove category"
                      className="p-1.5 rounded-arc-sm text-arc-neg bg-arc-neg-soft hover:brightness-110"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>

        {/* Growth Chart Section */}
        {showGrowthChart && (
          <div className="p-4 bg-arc-paper-2 rounded-arc-ctl ring-1 ring-arc-line">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-h2 text-arc-ink">Investment Growth</h4>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-arc-ink-2">Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="bg-arc-card text-arc-ink px-3 py-1 border border-arc-line rounded-arc-ctl focus:outline-none focus:ring-2 focus:ring-arc-accent"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Monthly Values Editor (only in edit mode) */}
            {canEdit && (
              <div className="mb-4">
                <h5 className="text-sm font-medium text-arc-ink mb-2">Monthly Values for {selectedYear}</h5>
                <div className="mb-3 flex gap-3 p-3 rounded-arc-ctl bg-arc-accent-soft">
                  <Lightbulb size={16} className="text-arc-accent-ink shrink-0 mt-0.5" />
                  <p className="text-sm text-arc-ink-2">
                    <strong className="text-arc-ink">Tip:</strong> Your current portfolio total (<span className="tnum">${totalInvestments.toLocaleString()}</span>) is automatically synced to the current month. Edit other months to track historical growth.
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(month => {
                    const isCurrentMonth = month === new Date().toLocaleDateString('en-US', { month: 'short' }) && selectedYear === new Date().getFullYear();
                    return (
                      <div key={month} className="flex flex-col">
                        <label className={`text-xs mb-1 ${isCurrentMonth ? 'text-arc-accent-ink font-semibold' : 'text-arc-ink-3'}`}>
                          {month} {isCurrentMonth && '(Current)'}
                        </label>
                        <EditableInput
                          value={isCurrentMonth ? totalInvestments : (investmentHistory[selectedYear]?.[month] || 0)}
                          onChange={(val) => updateMonthlyValue(month, val)}
                          className={`w-full text-right text-sm ${isCurrentMonth ? 'bg-arc-accent-soft border-arc-accent' : ''}`}
                          disabled={isCurrentMonth}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Investment Growth — GlowArea, blue tab accent, gains-grade glow */}
            <SafeChart chartsReady={chartsReady}>
              <GlowArea
                data={growthChartData}
                xKey="month"
                height={250}
                series={[{ key: 'total', name: 'Portfolio Value', tone: 'blue' }]}
                yTickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                valueFormatter={(v) => `$${Number(v).toLocaleString()}`}
              />
            </SafeChart>
          </div>
        )}

        {/* Layout grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Allocation donut — categorical NEON glow set (one color per category) */}
          <SafeChart chartsReady={chartsReady}>
            <GlowDonut
              data={pieChartData}
              dataKey="amount"
              nameKey="name"
              height={450}
              valueFormatter={(v) => `$${Number(v).toLocaleString()}`}
            />
          </SafeChart>

          {/* Editable list */}
          <div className="space-y-2">
            {filteredInvestmentsWithIndices.map(({ inv, originalIndex }) => (
              <div
                key={originalIndex}
                className="flex justify-between items-center p-3 rounded-arc-ctl ring-1 ring-arc-line"
              >
                <div className="flex-1">
                  <p className="font-medium text-arc-ink">
                    {canEdit ? (
                      <EditableInput
                        type="text"
                        value={inv.name}
                        onChange={(v) => updateInvestment(originalIndex, 'name', v)}
                        className="font-medium w-full"
                      />
                    ) : (
                      inv.name
                    )}
                  </p>
                  <div className="text-sm text-arc-ink-3 flex items-center gap-2">
                    {canEdit ? (
                      <select
                        value={inv.type}
                        onChange={(e) => updateInvestment(originalIndex, 'type', e.target.value)}
                        className="text-sm bg-arc-card text-arc-ink border border-arc-line rounded-arc-ctl px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-arc-accent"
                      >
                        {investmentCategories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    ) : (
                      <Badge tone="accent">{inv.type}</Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold tnum text-arc-accent-ink">
                    {canEdit ? (
                      <EditableInput
                        value={inv.amount}
                        onChange={(v) => updateInvestment(originalIndex, 'amount', v)}
                        className="w-24 text-right"
                      />
                    ) : (
                      `$${inv.amount.toLocaleString()}`
                    )}
                  </p>
                  {canEdit && (
                    <button
                      onClick={() => handleRemoveInvestment(originalIndex)}
                      className="text-arc-neg hover:brightness-110 p-1"
                      title="Remove investment"
                      aria-label="Remove investment"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Total */}
        <div className="text-center p-4 bg-arc-accent-soft rounded-arc-ctl ring-1 ring-arc-line">
          <SectionLabel className="text-arc-ink-3">Total Portfolio Value</SectionLabel>
          <p className="text-hero-num tnum font-bold text-arc-accent-ink mt-1">
            ${combinedTotal.toLocaleString()}
          </p>
          {assignedWalletsTotal > 0 && (
            <p className="text-xs tnum text-arc-ink-3 mt-1">
              Manual entries ${totalInvestments.toLocaleString()} · On-chain wallets ${assignedWalletsTotal.toLocaleString()}
            </p>
          )}
        </div>
      </Card>

      {/* Contextual recommendations: where to actually buy crypto.
          Investments tab is high-intent — users here are looking at where
          their money sits and where to put more. */}
      {(() => {
        const exchanges = AFFILIATE_LINKS.filter(
          (l) => !l.skip && l.category === "exchanges"
        );
        if (exchanges.length === 0) return null;
        return (
          <Card hover={false}>
            <SectionLabel className="mb-1">Recommended · where to buy</SectionLabel>
            <p className="text-sm text-arc-ink-3 mb-4">
              Two exchanges Swami uses to add to his stack
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {exchanges.map((l) => (
                <AffiliateCard key={l.id} link={l} medium="investments-tab" />
              ))}
            </div>
          </Card>
        );
      })()}
    </div>
  );
};

export default InvestmentsTab;